/** Default client-side sort for CourseDetail sub-tables. */
export const COURSE_DETAIL_LIST_DEFAULTS = {
  sortBy: 'name',
  sortDir: 'asc',
};

/** Sort keys for the Assessments tab table. */
export const ASSESSMENT_SORT_FIELDS = {
  name: 'name',
  questions: 'questions',
  duration: 'duration',
  submissions: 'submissions',
  avg: 'avg',
  status: 'status',
};

/** Sort keys for Enrollees and Gradebook tab tables. */
export const ENROLLEE_SORT_FIELDS = {
  name: 'name',
  enrolled: 'enrolled',
  progress: 'progress',
  assessmentProgress: 'assessmentProgress',
  trainingProgress: 'trainingProgress',
  score: 'score',
  status: 'status',
};

/** Sort keys for the Training tab table inside CourseDetail. */
export const COURSE_TRAINING_SORT_FIELDS = {
  name: 'name',
  start: 'start',
  due: 'due',
  completion: 'completion',
  domains: 'domains',
  questions: 'questions',
  status: 'status',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextCourseDetailSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || COURSE_DETAIL_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/** Parses a millisecond-timestamp string into a number (0 when missing/invalid). */
function toTimestamp(value) {
  if (!value) return 0;
  const ts = parseInt(value, 10);
  return Number.isNaN(ts) ? 0 : ts;
}

/** Resolves display/sort name for an enrollee row. */
function enrolleeName(row) {
  const email = row.studentEmail || row.email || '';
  return row.name || `${row.firstName || ''} ${row.lastName || ''}`.trim() || email;
}

/** Resolves status string for an assessment row. */
function assessmentStatus(row) {
  return row.status || (row.active === 1 ? 'current' : 'closed');
}

/** Resolves status string for an enrollee row. */
function enrolleeStatus(row) {
  return row.status || (row.activestatus === 0 ? 'inactive' : 'active');
}

function sortRowsLocally(rows, sortBy, sortDir, valueFn) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || COURSE_DETAIL_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = valueFn(a, field);
    const bv = valueFn(b, field);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}

function assessmentSortValue(row, sortBy) {
  switch (sortBy) {
    case ASSESSMENT_SORT_FIELDS.questions:
      return Number(row.questions) || 0;
    case ASSESSMENT_SORT_FIELDS.duration:
      return Number(row.duration) || 0;
    case ASSESSMENT_SORT_FIELDS.submissions:
      return Number(row.submitted) || 0;
    case ASSESSMENT_SORT_FIELDS.avg:
      return Number(row.avg) || 0;
    case ASSESSMENT_SORT_FIELDS.status:
      return assessmentStatus(row);
    case ASSESSMENT_SORT_FIELDS.name:
    default:
      return row.name ?? '';
  }
}

function enrolleeSortValue(row, sortBy) {
  switch (sortBy) {
    case ENROLLEE_SORT_FIELDS.enrolled:
      return toTimestamp(row.enrolled);
    case ENROLLEE_SORT_FIELDS.progress:
      return Number(row.progress) || 0;
    case ENROLLEE_SORT_FIELDS.assessmentProgress:
      return Number(row.assessmentProgress) || 0;
    case ENROLLEE_SORT_FIELDS.trainingProgress:
      return Number(row.trainingProgress) || 0;
    case ENROLLEE_SORT_FIELDS.score:
      return Number(row.score) || 0;
    case ENROLLEE_SORT_FIELDS.status:
      return enrolleeStatus(row);
    case ENROLLEE_SORT_FIELDS.name:
    default:
      return enrolleeName(row);
  }
}

function courseTrainingSortValue(row, sortBy) {
  switch (sortBy) {
    case COURSE_TRAINING_SORT_FIELDS.start:
      return toTimestamp(row.startDate);
    case COURSE_TRAINING_SORT_FIELDS.due:
      return toTimestamp(row.completionDate);
    case COURSE_TRAINING_SORT_FIELDS.completion:
      return Number(row.completionPercent) || 0;
    case COURSE_TRAINING_SORT_FIELDS.domains:
      return (row.domains || []).length;
    case COURSE_TRAINING_SORT_FIELDS.questions:
      return (row.questions || []).length;
    case COURSE_TRAINING_SORT_FIELDS.status:
      return row.active === 1 ? 1 : 0;
    case COURSE_TRAINING_SORT_FIELDS.name:
    default:
      return row.trainingName ?? '';
  }
}

/** Sorts assessment rows in memory — no API round-trip. */
export function sortAssessmentsLocally(rows, sortBy, sortDir) {
  return sortRowsLocally(rows, sortBy, sortDir, assessmentSortValue);
}

/** Sorts enrollee rows in memory — no API round-trip. */
export function sortEnrolleesLocally(rows, sortBy, sortDir) {
  return sortRowsLocally(rows, sortBy, sortDir, enrolleeSortValue);
}

/** Sorts course-scoped training rows in memory — no API round-trip. */
export function sortCourseTrainingsLocally(rows, sortBy, sortDir) {
  return sortRowsLocally(rows, sortBy, sortDir, courseTrainingSortValue);
}
